import { useCallback, useEffect, useState } from 'react'
import { get } from './lib/api'
import { family as familyApi } from './lib/chores'
import { me, signOut, type User } from './lib/auth'
import { t } from './lib/i18n'
import SignIn from './components/SignIn'
import Household from './components/Household'
import ParentHome from './components/ParentHome'
import KidHome from './components/KidHome'
import Account from './components/Account'
import Admin from './components/Admin'
import EnvBanner from './components/EnvBanner'
import UpdateBar from './components/UpdateBar'
import InstallPrompt from './components/InstallPrompt'
import DebugPanel from './components/DebugPanel'
import ErrorBoundary from './components/ErrorBoundary'

export interface Health {
  app: string
  environment: 'local' | 'beta' | 'production'
  version: string
  commit: string
}

type Tab = 'home' | 'account' | 'admin'

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [tab, setTab] = useState<Tab>('home')
  const [hasFamily, setHasFamily] = useState<boolean | null>(null)

  const refresh = useCallback(async () => {
    const u = await me()
    setUser(u)
    if (!u) { setHasFamily(null); return }
    // Everything in this app is scoped to a household, so knowing whether there
    // is one decides which screen the user even sees.
    try { setHasFamily((await familyApi.show()).family !== null) } catch { setHasFamily(false) }
  }, [])

  useEffect(() => {
    void (async () => {
      // Both in parallel: neither depends on the other, and the boot is the one
      // moment the user is definitely waiting.
      const [u, h] = await Promise.all([
        me(),
        get<Health>('/api/health').catch(() => null),
      ])
      setUser(u)
      setHealth(h)
      if (u) {
        try { setHasFamily((await familyApi.show()).family !== null) } catch { setHasFamily(false) }
      }
      setReady(true)
    })()
  }, [])

  if (!ready) return <div className="boot">{t('app.loading')}</div>

  return (
    <ErrorBoundary>
      {/* Above everything: an app running old code is the cause of bug reports
          nobody can reproduce. */}
      <UpdateBar />
      <EnvBanner health={health} />

      <header className="bar">
        <strong>{health?.app ?? '84 Chores'}</strong>
        {user && (
          <nav>
            <button onClick={() => setTab('home')} aria-current={tab === 'home'}>Home</button>
            {!user.isDelegate && (
              <button onClick={() => setTab('account')} aria-current={tab === 'account'}>Account</button>
            )}
            {/* Capability-driven, so the tab is absent rather than present-and-denied. */}
            {user.capabilities.includes('admin.access') && (
              <button onClick={() => setTab('admin')} aria-current={tab === 'admin'}>{t('admin.title')}</button>
            )}
            <button onClick={async () => { await signOut(); await refresh() }}>{t('app.signOut')}</button>
          </nav>
        )}
      </header>

      <main>
        {!user && <SignIn onSignedIn={refresh} />}
        {user && tab === 'home' && hasFamily === false && !user.isDelegate && <Household onDone={refresh} />}
        {user && tab === 'home' && hasFamily === false && user.isDelegate && (
          // A child cannot found a household, and telling them to is nonsense.
          <section className="card">
            <h1>Hi {user.displayName}</h1>
            <p className="muted">Ask a parent to finish setting things up.</p>
          </section>
        )}
        {user && tab === 'home' && hasFamily && !user.isDelegate && <ParentHome onChange={refresh} />}
        {user && tab === 'home' && hasFamily && user.isDelegate && <KidHome name={user.displayName} />}
        {user && tab === 'account' && <Account user={user} />}
        {user && tab === 'admin' && <Admin />}
      </main>

      {/* Capability, not environment: the phone that needs diagnosing is always
          a real one in production. */}
      {user?.capabilities.includes('errors.read') && <DebugPanel environment={health?.environment ?? '—'} />}

      {/* Below the fold, not in the way. For an app not on a store yet. */}
      <InstallPrompt />

      <footer className="muted small">
        {health ? `${health.version} · ${health.commit} · ${health.environment}` : ''}
      </footer>
    </ErrorBoundary>
  )
}
